import { BcatGrantType, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Benefit Catalog Grant Type`;

type SharedKeys = Pick<
	LocDef,
	'portal' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.Benefit,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type CPBcatGrantTypeLocs =
	| 'CPBcatGrantTypeEnumManual'
	| 'CPBcatGrantTypeEnumOncePerCcat'
	| 'CPBcatGrantTypeEnumOncePerCcatAchievedLogic'
	| 'CPBcatGrantTypeEnumOncePerCcatAchievement'
	| 'CPBcatGrantTypeEnumOncePerCcatList';

export type SPBcatGrantTypeLocs =
	| 'SPBcatGrantTypeEnumManual'
	| 'SPBcatGrantTypeEnumOncePerCcat'
	| 'SPBcatGrantTypeEnumOncePerCcatAchievedLogic'
	| 'SPBcatGrantTypeEnumOncePerCcatAchievement'
	| 'SPBcatGrantTypeEnumOncePerCcatList';

export const cpBcatGrantTypeEnumLocs: LocDefs<CPBcatGrantTypeLocs> = {
	CPBcatGrantTypeEnumManual: {
		defaultValue: 'Manual',
		displayPath: `${dpRoot}|${BcatGrantType.Manual}`,
		description: `System value for '${BcatGrantType.Manual}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.Manual',
		...sharedCpLocData,
	},
	CPBcatGrantTypeEnumOncePerCcat: {
		defaultValue: 'Once per credential',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcat}`,
		description: `System value for '${BcatGrantType.OncePerCcat}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcat',
		...sharedCpLocData,
	},
	CPBcatGrantTypeEnumOncePerCcatAchievedLogic: {
		defaultValue: 'Once Per Logic',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcatAchievedLogic}`,
		description: `System value for '${BcatGrantType.OncePerCcatAchievedLogic}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcatAchievedLogic',
		...sharedCpLocData,
	},
	CPBcatGrantTypeEnumOncePerCcatAchievement: {
		defaultValue: 'Once per event',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcatAchievement}`,
		description: `System value for '${BcatGrantType.OncePerCcatAchievement}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcatAchievement',
		...sharedCpLocData,
	},
	CPBcatGrantTypeEnumOncePerCcatList: {
		defaultValue: 'Once',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcatList}`,
		description: `System value for '${BcatGrantType.OncePerCcatList}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcatList',
		...sharedCpLocData,
	},
};

export const spBcatGrantTypeEnumLocs: LocDefs<SPBcatGrantTypeLocs> = {
	SPBcatGrantTypeEnumManual: {
		defaultValue: 'Manual',
		displayPath: `${dpRoot}|${BcatGrantType.Manual}`,
		description: `System value for '${BcatGrantType.Manual}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.Manual',
		...sharedSpLocData,
	},
	SPBcatGrantTypeEnumOncePerCcat: {
		defaultValue: 'Once per credential',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcat}`,
		description: `System value for '${BcatGrantType.OncePerCcat}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcat',
		...sharedSpLocData,
	},
	SPBcatGrantTypeEnumOncePerCcatAchievedLogic: {
		defaultValue: 'Once Per Logic',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcatAchievedLogic}`,
		description: `System value for '${BcatGrantType.OncePerCcatAchievedLogic}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcatAchievedLogic',
		...sharedSpLocData,
	},
	SPBcatGrantTypeEnumOncePerCcatAchievement: {
		defaultValue: 'Once per event',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcatAchievement}`,
		description: `System value for '${BcatGrantType.OncePerCcatAchievement}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcatAchievement',
		...sharedSpLocData,
	},
	SPBcatGrantTypeEnumOncePerCcatList: {
		defaultValue: 'Once',
		displayPath: `${dpRoot}|${BcatGrantType.OncePerCcatList}`,
		description: `System value for '${BcatGrantType.OncePerCcatList}' Benefit Catalog grant type`,
		legacyName: 'Benefit/System.Enum.BcatGrantType.OncePerCcatList',
		...sharedSpLocData,
	},
};

export const BCAT_GRANT_TYPE_ENUM_LOCS = MapLocKeys<CPBcatGrantTypeLocs | SPBcatGrantTypeLocs>({
	...cpBcatGrantTypeEnumLocs,
	...spBcatGrantTypeEnumLocs,
});

export type CPBcatGrantTypeEnumLocType = LocMap<CPBcatGrantTypeLocs>;
export type SPBcatGrantTypeEnumLocType = LocMap<SPBcatGrantTypeLocs>;

export const cpBcatGrantTypeKeys: { [key in BcatGrantType]: keyof CPBcatGrantTypeEnumLocType } = {
	[BcatGrantType.Manual]: BCAT_GRANT_TYPE_ENUM_LOCS.CPBcatGrantTypeEnumManual,
	[BcatGrantType.OncePerCcat]: BCAT_GRANT_TYPE_ENUM_LOCS.CPBcatGrantTypeEnumOncePerCcat,
	[BcatGrantType.OncePerCcatAchievedLogic]:
		BCAT_GRANT_TYPE_ENUM_LOCS.CPBcatGrantTypeEnumOncePerCcatAchievedLogic,
	[BcatGrantType.OncePerCcatAchievement]:
		BCAT_GRANT_TYPE_ENUM_LOCS.CPBcatGrantTypeEnumOncePerCcatAchievement,
	[BcatGrantType.OncePerCcatList]: BCAT_GRANT_TYPE_ENUM_LOCS.CPBcatGrantTypeEnumOncePerCcatList,
};

export const spBcatGrantTypeKeys: { [key in BcatGrantType]: keyof SPBcatGrantTypeEnumLocType } = {
	[BcatGrantType.Manual]: BCAT_GRANT_TYPE_ENUM_LOCS.SPBcatGrantTypeEnumManual,
	[BcatGrantType.OncePerCcat]: BCAT_GRANT_TYPE_ENUM_LOCS.SPBcatGrantTypeEnumOncePerCcat,
	[BcatGrantType.OncePerCcatAchievedLogic]:
		BCAT_GRANT_TYPE_ENUM_LOCS.SPBcatGrantTypeEnumOncePerCcatAchievedLogic,
	[BcatGrantType.OncePerCcatAchievement]:
		BCAT_GRANT_TYPE_ENUM_LOCS.SPBcatGrantTypeEnumOncePerCcatAchievement,
	[BcatGrantType.OncePerCcatList]: BCAT_GRANT_TYPE_ENUM_LOCS.SPBcatGrantTypeEnumOncePerCcatList,
};
